// TODO: replace this module with axios at some point.
import { XMLParser } from 'fast-xml-parser'

import SignedFetch from './main-signed-fetch'

const parser = new XMLParser()

type UnPromisify<T> = T extends Promise<infer U> ? U : T

type Func = (...args: any) => any
interface ParallelOpts<T extends Func> {
  tasks: T[]
  amount: number
  onProgress: (progress: UnPromisify<ReturnType<T>>) => void
}

export interface UploadMetadata {
  [index: string]: string | number
}

export interface UploadProgress {
  part: number
  totalParts: number
}

export interface UploaderOptions {
  bucket: string
  key: string
  authURL: string
  retryCount?: number
  retryDelay?: number
  metadata?: UploadMetadata
}

export interface FileUploader {
  bucket: string
  authURL: string
  retryCount?: number
  retryDelay?: number
}

export interface FileUploadOptions {
  file: File
  key: string
  metadata?: UploadMetadata
  parallelAmount?: number
  onProgress?: (progress: UploadProgress) => void
}

type Part = { partNumber: number, etag: string }

const parallel = async <T extends Func>(
  { tasks, amount, onProgress }: ParallelOpts<T>, queue = [...tasks]
) => {
  const chain = async () => {
    while (queue.length) {
      const task = queue.shift()
      if (task) onProgress(await task())
    }
  }
  await Promise.all([...Array(amount || 1)].map(chain))
}

const getUploadId = (xmlData: string) => {
  const result = parser.parse(xmlData)
  return result?.InitiateMultipartUploadResult?.UploadId
}

const MIN_PART_SIZE = 5242880

const Uploader = (
  { bucket, key, authURL, retryCount, retryDelay, metadata = {} }: UploaderOptions
) => {
  let partNumber = 0
  let uploadId = 0
  const parts: Part[] = []
  const pendingPartsUploads: Promise<Response>[] = []

  const signedFetch = SignedFetch({ authURL, retryCount, retryDelay })

  const start = async () => {
    const url = `https://s3.amazonaws.com/${bucket}/${key}?uploads=`

    const formattedMetadata = Object
      .entries(metadata)
      .reduce((res, [ key, val ]) => {
        const avertFoolishness = key.replace(/^x-amz-meta-/, '')
        const betterKey = `x-amz-meta-${avertFoolishness}`
        res[betterKey] = val
        return res
      }, {} as UploadMetadata)

    const response = await signedFetch(url, {
      method: 'POST',
      headers: {
        ...formattedMetadata,
      },
    }).then(m => m.text())

    uploadId = await getUploadId(response)
  }

  const uploadPart = async (data: any) => {
    if (!uploadId) throw new Error(
      'no upload id for video upload. probably did not call start() successfully first'
    )
    const thisPartNumber = ++partNumber
    const url =
      `https://s3.amazonaws.com/${bucket}/${key}?partNumber=${thisPartNumber}&uploadId=${uploadId}`

    const uploadRequest = signedFetch(url, {
      method: 'PUT',
      body: data,
      unsignedPayload: true,
    })

    pendingPartsUploads.push(uploadRequest)
    const response = await uploadRequest

    parts.push({
      partNumber: thisPartNumber,
      etag: response.headers.get('etag')! //AWS will return etag here or we are gonna have a bad day
    })
  }

  const finish = async () => {
    if (!uploadId) throw new Error(
      'no upload id for video upload. probably did not call start() successfully first'
    )
    const url = `https://s3.amazonaws.com/${bucket}/${key}?uploadId=${uploadId}`

    await Promise.all(pendingPartsUploads)

    const xmlParts = parts.sort((a: any, b: any) => a.partNumber - b.partNumber).map(
      p => `<Part>
      <PartNumber>${p.partNumber}</PartNumber>
      <ETag>${p.etag}</ETag>
    </Part>`,
    )

    const payload = `<CompleteMultipartUpload>
      ${xmlParts.join('\n')}
    </CompleteMultipartUpload>`

    await signedFetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/xml; charset=UTF-8',
      },
      body: payload,
    })

    return key
  }

  return { key, start, uploadPart, finish, MIN_PART_SIZE }
}

Uploader.fileUploader = ({ bucket, authURL, retryCount, retryDelay }: FileUploader) => {
  const uploadFile = async (
    { file, key, metadata, parallelAmount = 0, onProgress = () => {} }: FileUploadOptions
  ) => {
    const uploader = Uploader({ bucket, key, authURL, metadata, retryCount, retryDelay })
    const totalParts = Math.ceil(file.size / uploader.MIN_PART_SIZE)

    const chunks = [...Array(totalParts)].map((_, ix) => {
      const start = ix * uploader.MIN_PART_SIZE
      const end = (ix + 1) * uploader.MIN_PART_SIZE
      const chunk = file.slice(start, end, file.type)
      return { part: ix + 1, chunk }
    })

    await uploader.start()

    await parallel({
      amount: parallelAmount,
      tasks: chunks.map(m => async () => {
        await uploader.uploadPart(m.chunk)
        return m.part
      }),
      onProgress: part => onProgress({ part, totalParts }),
    })

    await uploader.finish()
  }

  return { uploadFile }
}

export default Uploader
